import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type Column,
  DateRenderer,
  EmptyStation,
  type ExplorerDataProvider,
  MessageBar,
  NavigationExplorer,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AzureStorageAccountContainersDocument,
  type AzureStorageAccountContainersQuery,
  type AzureStorageAccountContainersQueryVariables,
} from '../../../../generated/hosting-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';

export type StorageContainer = NonNullable<
  NonNullable<
    NonNullable<
      AzureStorageAccountContainersQuery['azureStorageContainers']
    >['containers']
  >[number]
>;

const explorerColumns: Column<StorageContainer>[] = [
  { label: 'Name', propertyName: 'name', size: '1fr', sortable: false },
  {
    label: 'Public Read Access',
    propertyName: 'hasPublicReadAccess',
    size: '1fr',
    sortable: false,
  },
  {
    label: 'Modified At',
    propertyName: 'updatedAt',
    render: DateRenderer,
    size: '1fr',
    sortable: false,
  },
];

export const StorageAccountContainers: React.FC = () => {
  const { envId, storageAccountName, storageType } = useParams<{
    envId: string;
    storageAccountName: string;
    storageType: string;
    action: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of hosting-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.HOSTING_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const dataProvider: ExplorerDataProvider<StorageContainer> = {
    loadData: async ({ pagingInformation }) => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );

      const result = await envApolloClient.query<
        AzureStorageAccountContainersQuery,
        AzureStorageAccountContainersQueryVariables
      >({
        query: AzureStorageAccountContainersDocument,
        variables: {
          input: {
            storageAccountName: storageAccountName,
            provider: storageType,
          },
        },
        fetchPolicy: 'no-cache',
      });

      const data = result.data.azureStorageContainers?.containers ?? [];

      return {
        data: data as StorageContainer[],
        totalCount: result.data.azureStorageContainers?.containers?.length ?? 0,
        filteredCount:
          result.data.azureStorageContainers?.containers?.length ?? 0,
        hasMoreData: false,
        pagingInformation: pagingInformation,
      };
    },
  };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Containers">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <NavigationExplorer<StorageContainer>
          title="Containers"
          keyProperty="name"
          stationKey="StorageAccountContainersExplorer"
          columns={explorerColumns}
          dataProvider={dataProvider}
          calculateNavigateUrl={(item) =>
            `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/${storageType}/${storageAccountName}/containers/${item.name}`
          }
          onCreateAction={`/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/${storageType}/${storageAccountName}/containers/create`}
        />
      )}
    </>
  );
};

export const StorageAccountContainersCrumb: BreadcrumbResolver = () =>
  'Containers';
